import React from "react";
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer
} from "@paypal/react-paypal-js";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { isMobile } from "react-device-detect";

const PaypalButtonComponent = ({ onSuccess, onError }) => {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  const [isMobilePayPal, setIsMobilePayPal] = React.useState(false);

  React.useEffect(() => {
    // Detect mobile device more aggressively
    const mobileDetect = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobilePayPal(isMobileDevice || isSmallScreen);
      console.log('Mobile PayPal detection:', { isMobileDevice, isSmallScreen, userAgent: navigator.userAgent });
    };
    
    mobileDetect();
    window.addEventListener('resize', mobileDetect);
    return () => window.removeEventListener('resize', mobileDetect);
  }, []);

  // ✅ 1. Create Vault Setup Token with mobile-specific handling
  const getSetupToken = async () => {
    try {
      console.log('Creating setup token for mobile:', isMobilePayPal);
      
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/customer_token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({
            user_id: localStorage.getItem("logged_user_id"),
            context: "paypal_vault",
            mobile: isMobilePayPal,
            user_agent: navigator.userAgent
          })
        }
      );

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to get setup token");
      }

      console.log('Setup token created successfully for mobile');
      return data.data.id;
    } catch (error) {
      console.error("Setup token error (mobile):", error);
      console.log("Failed to initialize PayPal setup");
      throw error;
    }
  };

  // ✅ 2. Use vaultSetupToken dynamically here
  const handleApprove = async ({ vaultSetupToken }) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/create_payment_token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({
            setup_token: vaultSetupToken,
            user_id: localStorage.getItem("logged_user_id")
          })
        }
      );

      const result = await res.json();
      console.log("🔍 Response from create_payment_token:", result);

      // ✅ Adjusted to match your API's actual response
     if (!result?.success || !result?.token_id) {
  console.error("Unexpected server response:", result);
  return; // yaha bas exit kar do, user ko kuch mat dikhao
}


      console.log("✅ Payment method saved successfully!");
      onSuccess?.(); // Optional callback
    } catch (err) {
      console.error("❌ Payment token error:", err);
      console.log(err.message || "❌ Failed to save payment method");
      onError?.(err);
    }
  };

  if (isRejected)
    return (
      <Typography color="error" align="center" sx={{ fontSize: "0.8rem" }}>
        Failed to load PayPal. Please refresh and try again.
      </Typography>
    );

  if (isPending)
    return <Typography align="center" sx={{ fontSize: "0.8rem" }}>Loading PayPal button...</Typography>;

  return (
    <Box>
      {/* Mobile vault flow info */}
      {isMobilePayPal && (
        <Typography 
          variant="body2" 
          color="info.main" 
          sx={{ 
            mb: 1, 
            textAlign: "center", 
            fontSize: "0.75rem",
            backgroundColor: "info.light",
            p: 1,
            borderRadius: 1
          }}
        >
          📱 Mobile: PayPal vault flow will save your payment method
        </Typography>
      )}
      
      <PayPalButtons
        fundingSource="paypal"
        style={{
          layout: "vertical",
          shape: "pill",
          label: "paypal",
          height: isMobilePayPal ? 50 : 45,
          disableMaxWidth: true,
          color: "gold",
          tagline: false
        }}
        createVaultSetupToken={getSetupToken}
        onApprove={handleApprove}
        onCancel={() => {
          console.log("PayPal vault setup cancelled by user");
        }}
        onError={(err) => {
          console.error("PayPal vault error:", err);
          
          // Mobile-specific error handling
          let errorMessage = "Failed to save PayPal account";
          if (isMobilePayPal) {
            if (err.message?.includes("popup")) {
              errorMessage = "Please allow popups for PayPal vault setup";
            } else {
              errorMessage = "Mobile PayPal vault error. Please try again.";
            }
          }
          
          console.log(errorMessage);
          onError?.(new Error(errorMessage));
        }}
        onClick={(data, actions) => {
          console.log("PayPal vault button clicked - Mobile:", { isMobilePayPal, isMobile });
          return actions.resolve();
        }}
        onInit={(data, actions) => {
          console.log("PayPal vault initialized:", { 
            isMobilePayPal, 
            vault: true,
            userAgent: navigator.userAgent 
          });
        }}
      />
    </Box>
  );
};

export default function PaypalVaultSection({ onPaymentSaved, onError }) {
  const [isMobilePayPal, setIsMobilePayPal] = React.useState(false);

  React.useEffect(() => {
    // Detect mobile device but always use vault flow
    const mobileDetect = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      
      const mobile = isMobileDevice || isSmallScreen;
      setIsMobilePayPal(mobile);
      
      console.log('Mobile PayPal detection:', { 
        isMobileDevice, 
        isSmallScreen, 
        mobile, 
        userAgent: navigator.userAgent,
        vaultFlow: true
      });
    };
    
    mobileDetect();
    window.addEventListener('resize', mobileDetect);
    return () => window.removeEventListener('resize', mobileDetect);
  }, []);

  const initialOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    currency: "USD",
    components: "buttons",
    vault: true,  // CRITICAL: Always use vault for both mobile and desktop
    intent: "capture",
    // Ensure vault flow works on mobile
    "enable-funding": "paypal",
    "disable-funding": "credit,card,venmo,applepay,googlepay,paylater",
    // Remove flow: checkout - it breaks vaulting on mobile
    "user-experience": "default",
    "integration-date": "2024-03-27",
    "locale": "en_US",
    // Mobile viewport fixes but keep vault flow
    "mobile": "true",
    "tablet": "false",
    // Prevent popup blocking but maintain vault
    "disable-credit-card-entries": "true",
    "disable-card": "true",
    "debug": process.env.NODE_ENV === "development",
    // Vault-specific return URLs
    "return-url": `${window.location.origin}/paypal-return`,
    "cancel-url": `${window.location.origin}/paypal-cancel`
  };

  return (
    <Box mt={2}>
      <PayPalScriptProvider options={initialOptions}>
        <PaypalButtonComponent
          onSuccess={onPaymentSaved}
          onError={onError}
        />
      </PayPalScriptProvider>
    </Box>
  );
}
